import cartUtils from '../../utils/cart';
import { addToCart } from './addToCart';
import { finishRetouchingEdit, updateRetouchingSelection } from './v3Order';
import {
  selectRetouchingAddableForOrder,
  selectRetouchingLevelsForOrder,
} from '../../selectors';
import type { RetouchingRootState } from '../../selectors/retouchingState';
import {
  RetouchingPose,
  RetouchingProduct,
} from '../../components/v3/products/retouching/types';

interface ChipPose extends RetouchingPose {
  imageRecordType?: string;
}

// addToCart is untyped JS whose destructured arguments TypeScript infers as
// all required; the chip passes only the retouching subset.
const addRetouchingToCart = addToCart as unknown as (args: {
  studentId: number;
  orderKeyOverride: string;
  fullproductData: RetouchingProduct;
  retouchingPricingMode: 'per_image';
  poseIds: number[];
  poses: ChipPose[];
  quantity: number;
  redirectSource: 'stay';
  retouchingSources: Record<number, 'cart_chip'>;
}) => unknown;

// The store is untyped; this action reads only the retouching slices.
type GetState = () => unknown;
type Dispatch = (action: unknown) => unknown;

// Adds one cart photo to a retouching level on the order the chip is shown
// in, never on the globally selected student's order, and stays on the cart
// without adding a history entry.
// A photo already retouched, a photo the order cannot select, or an order the
// shared gate refuses is left unchanged.
export const addPhotoToRetouchingLevel =
  ({
    orderKey,
    poseId,
    productId,
    source,
  }: {
    orderKey: string;
    poseId: number;
    productId: number;
    source: 'cart_chip';
  }) =>
  (dispatch: Dispatch, getState: GetState): void => {
    const state = getState() as RetouchingRootState;
    const order = state.v3Order?.orders?.[orderKey];
    if (!order?.student || !selectRetouchingAddableForOrder(state, orderKey))
      return;
    if (cartUtils.retouchingLineForPose(order, poseId)) return;

    const product = selectRetouchingLevelsForOrder(state, orderKey).find(
      (level) => level.id === productId,
    );
    const selectable: ChipPose[] = cartUtils.distinctCartPoses(order);
    const pose = selectable.find(
      (candidate) => String(candidate.id) === String(poseId),
    );
    if (!product || !pose) return;

    const sources = { [pose.id]: source };
    const line = (order.cartItems || []).find(
      (item) => item.isRetouching && item.productId === productId,
    );

    if (line) {
      // A persisted line can carry ids without their pose objects; rebuild
      // the objects so the update keeps every photo already on the line.
      const linePoses = (line.poseIds || [])
        .map(
          (id) =>
            (line.poses || []).find((p) => String(p?.id) === String(id)) ||
            selectable.find((p) => String(p.id) === String(id)),
        )
        .filter((p): p is ChipPose => Boolean(p));
      dispatch(
        updateRetouchingSelection(
          orderKey,
          line.id,
          [...linePoses, pose],
          product.price,
          line.retouchingNotes,
          { sources },
        ),
      );
    } else {
      dispatch(
        addRetouchingToCart({
          studentId: order.student.id,
          orderKeyOverride: orderKey,
          fullproductData: product,
          retouchingPricingMode: 'per_image',
          poseIds: [pose.id],
          poses: [pose],
          quantity: 1,
          redirectSource: 'stay',
          retouchingSources: sources,
        }),
      );
    }

    dispatch(finishRetouchingEdit(orderKey, { stayOnCart: true }));
  };
